// PricingModal.tsx
// Shown when a user hits their tier limit or clicks Upgrade.
// Supports monthly/annual billing toggle with real Stripe checkout.

import React, { useState } from 'react';
import { useStripe } from '../hooks/useStripe';
import type { TierKey } from '../types';
import { TIER_LABELS } from '../utils/formatters';
import styles from '../styles/PricingModal.module.css';

interface PricingModalProps {
  currentTier: TierKey;
  onClose: () => void;
}

// ─── Pricing data ─────────────────────────────────────────────────────────────

const MONTHLY_PRICES: Record<TierKey, number> = {
  free: 0, reading_list: 8, dissertation: 18, lab: 49,
};

const ANNUAL_PRICES: Record<TierKey, number> = {
  free: 0, reading_list: 55, dissertation: 120, lab: 320,
};

const ANNUAL_SAVINGS: Record<TierKey, number> = {
  free: 0,
  reading_list: Math.round((1 - 55 / (8 * 12)) * 100),
  dissertation: Math.round((1 - 120 / (18 * 12)) * 100),
  lab: Math.round((1 - 320 / (49 * 12)) * 100),
};

const TIER_DESCRIPTIONS: Record<TierKey, string> = {
  free: '5 papers/month — get a feel for it',
  reading_list: '50 papers/month · batch up to 5',
  dissertation: '200 papers/month · batch up to 20 · Zotero import',
  lab: 'Unlimited papers · fair use policy · API access',
};

const TIER_FEATURES: Record<TierKey, string[]> = {
  free: ['5 evaluations/month', 'Single paper upload', 'All 6 scoring dimensions', 'Markdown export'],
  reading_list: ['50 evaluations/month', 'Batch upload up to 5', 'All priority modes', 'Markdown, CSV & Obsidian export'],
  dissertation: ['200 evaluations/month', 'Batch upload up to 20', 'Zotero import', 'Ranked reading plan', 'All export formats'],
  lab: ['Unlimited evaluations*', 'Unlimited batch size', 'API access', 'Everything in Dissertation', 'Priority support'],
};

const TIER_ORDER: TierKey[] = ['free', 'reading_list', 'dissertation', 'lab'];
const RECOMMENDED: TierKey = 'dissertation';

// ─── Component ───────────────────────────────────────────────────────────────

export default function PricingModal({ currentTier, onClose }: PricingModalProps) {
  const [billing, setBilling] = useState<'monthly' | 'annual'>('annual');
  const [expandedTier, setExpandedTier] = useState<TierKey | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const { loading, redirectToCheckout, redirectToPortal } = useStripe();

  const currentTierIndex = TIER_ORDER.indexOf(currentTier);

  const handleUpgrade = async (tierKey: TierKey) => {
    setLocalError(null);
    try {
      await redirectToCheckout(tierKey, billing);
    } catch (e: any) {
      setLocalError(e?.message || 'Something went wrong. Please try again.');
    }
  };

  const handleToggleBilling = (b: 'monthly' | 'annual') => {
    setLocalError(null);
    setBilling(b);
  };

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className={styles.header}>
          <div>
            <h2 className={styles.title}>Choose your plan</h2>
            <p className={styles.subtitle}>Stop reading papers that aren't worth your time.</p>
          </div>
          <button className={styles.btnClose} onClick={onClose}>✕</button>
        </div>

        {/* Billing toggle */}
        <div className={styles.billingToggle}>
          <button
            className={`${styles.billingBtn} ${billing === 'monthly' ? styles.billingBtnActive : ''}`}
            onClick={() => handleToggleBilling('monthly')}
          >
            Monthly
          </button>
          <button
            className={`${styles.billingBtn} ${billing === 'annual' ? styles.billingBtnActive : ''}`}
            onClick={() => handleToggleBilling('annual')}
          >
            Annual
            <span className={styles.saveBadge}>SAVE 43–46%</span>
          </button>
        </div>

        {/* Error */}
        {localError && <div className={styles.error}>{localError}</div>}

        {/* Tier cards */}
        <div className={styles.tierList}>
          {TIER_ORDER.map(tierKey => {
            const isCurrent = tierKey === currentTier;
            const isDowngrade = TIER_ORDER.indexOf(tierKey) < currentTierIndex;
            const isRecommended = tierKey === RECOMMENDED;
            const isExpanded = expandedTier === tierKey;
            const monthlyPrice = MONTHLY_PRICES[tierKey];
            const annualPrice = ANNUAL_PRICES[tierKey];
            const annualMonthly = tierKey === 'free' ? 0 : +(annualPrice / 12).toFixed(2);
            const savings = ANNUAL_SAVINGS[tierKey];

            const cardClass = [
              styles.tierCard,
              isRecommended && !isCurrent ? styles.tierCardRecommended : '',
              isCurrent ? styles.tierCardCurrent : '',
              isDowngrade ? styles.tierCardDowngrade : '',
            ].filter(Boolean).join(' ');

            const bodyClass = [
              styles.tierCardBody,
              isRecommended ? styles.tierCardBodyRecommended : '',
            ].filter(Boolean).join(' ');

            return (
              <div key={tierKey} className={cardClass}>

                {/* Recommended badge */}
                {isRecommended && !isCurrent && (
                  <div className={styles.recommendedBadge}>
                    ★ Most popular for researchers
                  </div>
                )}

                <div className={bodyClass}>

                  {/* Left: name + description */}
                  <div className={styles.tierInfo}>
                    <div className={`${styles.tierName} ${isRecommended ? styles.tierNameRecommended : ''}`}>
                      {TIER_LABELS[tierKey]}
                      {isCurrent && <span className={styles.currentBadge}>Current</span>}
                    </div>
                    <div className={`${styles.tierDesc} ${isRecommended ? styles.tierDescRecommended : ''}`}>
                      {TIER_DESCRIPTIONS[tierKey]}
                    </div>
                    <button
                      className={styles.btnFeatures}
                      onClick={() => setExpandedTier(isExpanded ? null : tierKey)}
                    >
                      {isExpanded ? 'Hide features ↑' : 'See features ↓'}
                    </button>
                  </div>

                  {/* Right: price + CTA */}
                  <div className={styles.tierPricing}>
                    {monthlyPrice === 0 ? (
                      <div className={styles.priceFree}>Free</div>
                    ) : billing === 'monthly' ? (
                      <>
                        <div className={`${styles.priceMain} ${isRecommended ? styles.priceMainLarge : ''}`}>
                          ${monthlyPrice}<span className={styles.priceSuffix}>/mo</span>
                        </div>
                        <div className={styles.priceBilled}>billed monthly</div>
                      </>
                    ) : (
                      <>
                        <div className={`${styles.priceMain} ${isRecommended ? styles.priceMainLarge : ''}`}>
                          ${annualPrice}<span className={styles.priceSuffix}>/yr</span>
                        </div>
                        <div className={styles.priceSavings}>save {savings}%</div>
                        <div className={styles.priceEffective}>${annualMonthly.toFixed(2)}/mo effective</div>
                      </>
                    )}

                    {!isCurrent && !isDowngrade && tierKey !== 'free' && (
                      <button
                        onClick={() => handleUpgrade(tierKey)}
                        disabled={loading}
                        className={[
                          styles.btnUpgrade,
                          isRecommended ? styles.btnUpgradeRecommended : '',
                          loading ? styles.btnUpgradeDisabled : '',
                        ].filter(Boolean).join(' ')}
                      >
                        {loading ? 'Loading…' : 'Upgrade →'}
                      </button>
                    )}
                  </div>
                </div>

                {/* Expanded features */}
                {isExpanded && (
                  <div className={styles.featuresExpanded}>
                    <ul className={`${styles.featuresList} ${TIER_FEATURES[tierKey].length <= 3 ? styles.featuresListShort : ''}`}>
                      {TIER_FEATURES[tierKey].map(feature => (
                        <li key={feature} className={styles.featureItem}>
                          <span className={styles.featureCheck}>✓</span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          {currentTier !== 'free' && (
            <button
              onClick={async () => { setLocalError(null); await redirectToPortal(); }}
              disabled={loading}
              className={styles.btnManageBilling}
            >
              Manage billing / cancel subscription
            </button>
          )}
          <p className={styles.footerNote}>
            Payments secured by Stripe · Cancel anytime ·{' '}
            {currentTier === 'lab'
              ? '*Fair use policy applies above 500 papers/month'
              : 'No hidden fees'}
          </p>
        </div>
      </div>
    </div>
  );
}
